//src/components/AllProperties.js
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useSearchParams } from 'react-router-dom';
import axiosInstance from '../axios';
import useDebounce from '../hooks/useDebounce';
import { getFrontendBaseUrl } from '../utils/propertyUrl';

import PropertyCard from './property/PropertyCard';
import PropertyCardSkeleton from './property/PropertyCardSkeleton';
import PropertyEmptyState from './property/PropertyEmptyState';
import PropertyFilters from './property/PropertyFilters';

const LIST_ENDPOINT = '/listing-properties/all-properties';
const PAGE_SIZE = 12;

const DEFAULT_FILTERS = {
    search: '',
    status: 'all',
    sort: 'latest',
    propertyType: '',
    city: '',
    province: '',
    district: '',
};

const uniq = (arr) => [...new Set(arr.filter(Boolean))];

const buildRobotsTag = (filters) => {
    const hasActiveFilter =
        Boolean(filters.search?.trim()) ||
        filters.status !== 'all' ||
        Boolean(filters.propertyType) ||
        Boolean(filters.city) ||
        Boolean(filters.province) ||
        Boolean(filters.district);

    return hasActiveFilter ? 'noindex,follow' : 'index,follow';
};

const buildItemListSchema = (properties) => {
    if (!Array.isArray(properties) || properties.length === 0) return null;

    return {
        '@context': 'https://schema.org',
        '@type': 'ItemList',
        name: 'Semua Properti - Urbania',
        itemListElement: properties.map((property, index) => ({
            '@type': 'ListItem',
            position: index + 1,
            url: property.public_url || `${getFrontendBaseUrl()}/properties/${property.slug || property.id}`,
            name: property.title || 'Properti',
        })),
    };
};

const AllProperties = () => {
    const [searchParams] = useSearchParams();
    const [properties, setProperties] = useState([]);
    const [loading, setLoading] = useState(true);
    const [loadingMore, setLoadingMore] = useState(false);
    const [error, setError] = useState('');

    const [nextCursor, setNextCursor] = useState(null);
    const [hasMore, setHasMore] = useState(true);

    const [filters, setFilters] = useState(() => ({
        ...DEFAULT_FILTERS,
        propertyType: searchParams.get('category') || '',
    }));

    const debouncedSearch = useDebounce(filters.search, 350);

    const sentinelRef = useRef(null);

    const currentFilters = useMemo(
        () => ({
            ...filters,
            search: debouncedSearch,
        }),
        [filters, debouncedSearch]
    );

    const fetchProperties = useCallback(
        async ({ cursor = null, append = false } = {}) => {
            try {
                setError('');

                if (append) {
                    setLoadingMore(true);
                } else {
                    setLoading(true);
                }

                const response = await axiosInstance.get(LIST_ENDPOINT, {
                    params: {
                        per_page: PAGE_SIZE,
                        ...(cursor ? { cursor } : {}),
                        ...(currentFilters.search ? { search: currentFilters.search } : {}),
                        ...(currentFilters.status !== 'all' ? { status: currentFilters.status } : {}),
                        ...(currentFilters.sort ? { sort: currentFilters.sort } : {}),
                        ...(currentFilters.propertyType ? { property_type: currentFilters.propertyType } : {}),
                        ...(currentFilters.city ? { city: currentFilters.city } : {}),
                        ...(currentFilters.province ? { province: currentFilters.province } : {}),
                        ...(currentFilters.district ? { district: currentFilters.district } : {}),
                    },
                    headers: {
                        'X-Platform': 'Web',
                    },
                });

                const payload = response.data || {};
                const items = Array.isArray(payload.data) ? payload.data : [];
                const meta = payload.meta || {};

                setProperties((prev) => {
                    if (!append) return items;

                    const merged = [...prev];
                    items.forEach((item) => {
                        if (!merged.some((existing) => existing.id === item.id)) {
                            merged.push(item);
                        }
                    });
                    return merged;
                });

                setNextCursor(meta.next_cursor ?? null);
                setHasMore(Boolean(meta.has_more));
            } catch (err) {
                console.error('Error fetching properties:', err);
                setError(
                    err?.response?.status === 404
                        ? 'Endpoint daftar properti tidak ditemukan. Silakan periksa rute API backend Anda.'
                        : 'Gagal memuat properti. Silakan coba lagi.'
                );
            } finally {
                setLoading(false);
                setLoadingMore(false);
            }
        },
        [currentFilters]
    );

    useEffect(() => {
        setProperties([]);
        setNextCursor(null);
        setHasMore(true);
        fetchProperties({ cursor: null, append: false });
    }, [fetchProperties]);

    useEffect(() => {
        if (!hasMore || loadingMore || loading) return;

        const sentinel = sentinelRef.current;
        if (!sentinel) return;

        const observer = new IntersectionObserver(
            (entries) => {
                if (entries[0].isIntersecting && hasMore && !loadingMore) {
                    fetchProperties({ cursor: nextCursor, append: true });
                }
            },
            { rootMargin: '200px' }
        );

        observer.observe(sentinel);
        return () => observer.disconnect();
    }, [hasMore, loadingMore, loading, nextCursor, fetchProperties]);

    const filterOptions = useMemo(() => {
        return {
            propertyTypes: uniq(properties.map((p) => p.property_type)),
            cities: uniq(properties.map((p) => p.city)),
            provinces: uniq(properties.map((p) => p.province)),
            districts: uniq(properties.map((p) => p.district)),
        };
    }, [properties]);

    const schema = useMemo(() => buildItemListSchema(properties), [properties]);

    const handleClearFilters = () => {
        setFilters(DEFAULT_FILTERS);
    };

    const setSearch = (value) => {
        setFilters((prev) => ({ ...prev, search: value }));
    };

    const setStatus = (value) => {
        setFilters((prev) => ({ ...prev, status: value }));
    };

    const setSort = (value) => {
        setFilters((prev) => ({ ...prev, sort: value }));
    };

    const setPropertyType = (value) => {
        setFilters((prev) => ({ ...prev, propertyType: value }));
    };

    const setCity = (value) => {
        setFilters((prev) => ({ ...prev, city: value }));
    };

    const setProvince = (value) => {
        setFilters((prev) => ({ ...prev, province: value }));
    };

    const setDistrict = (value) => {
        setFilters((prev) => ({ ...prev, district: value }));
    };

    const totalVisible = properties.length;
    const isFiltered =
        Boolean(currentFilters.search) ||
        currentFilters.status !== 'all' ||
        Boolean(currentFilters.propertyType) ||
        Boolean(currentFilters.city) ||
        Boolean(currentFilters.province) ||
        Boolean(currentFilters.district);

    return (
        <main className="property-page">
            <Helmet>
                <title>Semua Properti - Urbania</title>
                <meta
                    name="description"
                    content="Temukan properti premium untuk dijual dan disewa. Jelajahi koleksi properti residensial dan komersial terbaik kami."
                />
                <meta name="robots" content={buildRobotsTag(currentFilters)} />
                <link rel="canonical" href={`${getFrontendBaseUrl()}/properties`} />
            </Helmet>

            <section className="property-hero">
                <div className="container">
                    <div className="property-hero-content">
                        <div>
                            <p className="hero-kicker">Temukan Properti Terbaik</p>
                            <h1 className="hero-title">Semua Properti</h1>
                        </div>
                    </div>

                    <PropertyFilters
                        search={filters.search}
                        onSearchChange={setSearch}
                        status={filters.status}
                        onStatusChange={setStatus}
                        sort={filters.sort}
                        onSortChange={setSort}
                        propertyType={filters.propertyType}
                        onPropertyTypeChange={setPropertyType}
                        city={filters.city}
                        onCityChange={setCity}
                        province={filters.province}
                        onProvinceChange={setProvince}
                        district={filters.district}
                        onDistrictChange={setDistrict}
                        propertyTypes={filterOptions.propertyTypes}
                        cities={filterOptions.cities}
                        provinces={filterOptions.provinces}
                        districts={filterOptions.districts}
                        onClear={handleClearFilters}
                    />
                </div>
            </section>

            <section className="property-content">
                <div className="container">
                    <div className="property-summary">
                        <span>{totalVisible} properti ditemukan</span>
                        {isFiltered && <span className="muted">Filter aktif</span>}
                    </div>

                    {error ? (
                        <div className="property-error-state">{error}</div>
                    ) : loading ? (
                        <div className="property-grid">
                            {Array.from({ length: PAGE_SIZE }).map((_, i) => (
                                <PropertyCardSkeleton key={i} />
                            ))}
                        </div>
                    ) : properties.length === 0 ? (
                        <PropertyEmptyState />
                    ) : (
                        <>
                            <div className="property-grid">
                                {properties.map((property) => (
                                    <PropertyCard key={property.id} property={property} />
                                ))}
                            </div>

                            {loadingMore && (
                                <div className="property-pagination">
                                    <div className="property-pagination__info">Memuat properti tambahan...</div>
                                </div>
                            )}

                            {!hasMore && properties.length > 0 && (
                                <div className="property-pagination">
                                    <div className="property-pagination__info">Semua properti telah ditampilkan</div>
                                </div>
                            )}

                            <div ref={sentinelRef} className="infinite-scroll-sentinel" />
                        </>
                    )}
                </div>
            </section>

            {schema ? <script type="application/ld+json">{JSON.stringify(schema)}</script> : null}
        </main>
    );
};

export default AllProperties;
